// Long STIBO texts (AT_item_description and the like) arrive as HTML - and that HTML ESCAPED:
// "&lt;ul&gt;&lt;li&gt;Flächen-Abstandhalter …&lt;/li&gt;…". Shown as-is it read as entity soup
// (see chat, article 699223522). These helpers turn it back into text and tell whether markup is
// left that is worth rendering; components/RichText.tsx renders it, the search uses the plain text.
//
// Deliberately regex-based and free of the DOM: richTextToPlain runs over every row of the search
// index (100.000+), and decoding a handful of entities needs no parser.

const NAMED_ENTITIES: Record<string, string> = {
    lt: '<',
    gt: '>',
    amp: '&',
    quot: '"',
    apos: '\'',
    nbsp: '\u00a0'
};

const ENTITY = /&(lt|gt|amp|quot|apos|nbsp|#\d+|#x[0-9a-f]+);/gi;

// Markup we render (components/RichText.tsx) - anything else stays text or is dropped there.
const RENDERED_TAG = /<\/?(ul|ol|li|p|br|b|strong|i|em|u|sub|sup|div|span)\b[^>]*>/i;

const ANY_TAG = /<\/?[a-z][a-z0-9]*\b[^>]*>/gi;
const BLOCK_TAG = /<\/?(ul|ol|li|p|br|div)\b[^>]*>/gi;

const decodeOnce = (text: string): string =>
    text.replace(ENTITY, (match, name: string) => {
        const lower = name.toLowerCase();
        if (lower.startsWith('#x')) {
            return String.fromCodePoint(parseInt(lower.slice(2), 16));
        }
        if (lower.startsWith('#')) {
            return String.fromCodePoint(parseInt(lower.slice(1), 10));
        }
        return NAMED_ENTITIES[lower] ?? match;
    });

/**
 * Entities decoded - twice when the first pass still leaves an escaped tag ("&amp;lt;ul&amp;gt;"
 * is the same text escaped once more on the way through STIBO) - and whether markup remains.
 */
export const normalizeRichText = (value: string): { text: string; html: boolean } => {
    if (!value.includes('&') && !value.includes('<')) {
        return { text: value, html: false };
    }
    let text = decodeOnce(value);
    if (/&lt;\/?[a-z]/i.test(text)) {
        text = decodeOnce(text);
    }
    return { text, html: RENDERED_TAG.test(text) };
};

/** The same text without any markup - for the full-text search, where tags would only match noise. */
export const richTextToPlain = (value: string): string => {
    const { text, html } = normalizeRichText(value);
    if (!html && !text.includes('<')) {
        return text;
    }
    // Inside markup an entity is text again ("&amp;" in a list item is "&") - the same the
    // browser shows in components/RichText.tsx.
    const stripped = text.replace(BLOCK_TAG, ' ').replace(ANY_TAG, '').replace(/\s+/g, ' ').trim();
    return html ? decodeOnce(stripped) : stripped;
};
